'use client'

import { useEffect, useMemo, useRef } from 'react'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useAnalytics as useAnalyticsShared } from '@b-stock/fe-core/bstock-react'

import { useBotDetection } from './BotDetectionProvider'
import { useInteractionGate } from './InteractionGateProvider'

const NOOP_ANALYTICS: ReturnType<typeof useAnalyticsShared> = {
  pageViewed: () => {},
  errorViewed: () => {},
  trackButtonClicked: () => {},
  identifyUser: () => {},
  track: () => {},
}

export function useAnalytics() {
  const decodedAccessToken = useDecodedAccessToken()
  const accountId = decodedAccessToken?.accountId
  const personId = decodedAccessToken?.personId
  const { excludeFromAnalytics } = useBotDetection()
  const { hasInteracted } = useInteractionGate()

  const analytics = useAnalyticsShared({
    accountId,
    personId,
  })

  const pendingRef = useRef<Array<() => void>>([])

  useEffect(() => {
    if (hasInteracted && pendingRef.current.length > 0) {
      pendingRef.current.forEach((fn) => fn())
      pendingRef.current = []
    }
  }, [hasInteracted])

  return useMemo(() => {
    if (excludeFromAnalytics) return NOOP_ANALYTICS

    if (!hasInteracted) {
      return {
        pageViewed: (...args: Parameters<typeof analytics.pageViewed>) =>
          void pendingRef.current.push(() => analytics.pageViewed(...args)),
        errorViewed: (...args: Parameters<typeof analytics.errorViewed>) =>
          void pendingRef.current.push(() => analytics.errorViewed(...args)),
        trackButtonClicked: (
          ...args: Parameters<typeof analytics.trackButtonClicked>
        ) =>
          void pendingRef.current.push(() =>
            analytics.trackButtonClicked(...args)
          ),
        identifyUser: (...args: Parameters<typeof analytics.identifyUser>) =>
          void pendingRef.current.push(() => analytics.identifyUser(...args)),
        track: (...args: Parameters<typeof analytics.track>) =>
          void pendingRef.current.push(() => analytics.track(...args)),
      }
    }

    return analytics
  }, [analytics, excludeFromAnalytics, hasInteracted])
}
